<template>
  <div class="flex h-screen flex-col overflow-y-hidden bg-white">
    <app-header class="flex overflow-visible">
      <div class="w-full lg:w-1/2 p-5">
        <bot-selector :default-bot-id="botId"
                      :options-filter="emailBotFilter"
                      :get-bot-label="getEmailBotLabel"
                      exact-bot-id
                      @change="changeBot"
                      class="w-full"
        />
      </div>
    </app-header>
    <div class="h-full">
      <div class="flex flex-col lg:flex-row w-full h-full">
        <shadowed-wrapper class="flex flex-col min-h-0 overflow-y-auto p-5 w-full lg:w-1/2 bg-background">
          <span class="text-gray-dark font-bold text-xl shrink-0">Preview</span>
          <div class="w-full overflow-hidden border border-solid border-gray-border rounded shadow-sm bg-white font-sans text-gray-dark mt-3 p-5"
               :class="{ 'border-soft': isDisabled }"
          >
            <div ref="previewContainer"
                 title="preview"
                 class="w-full"
            >
              <div :style="previewViewportStyle">
                <div ref="previewContent"
                     v-html="emailPreview"
                     class="email-template-preview-content text-xl"
                     :style="previewContentStyle"
                />
              </div>
            </div>
          </div>
        </shadowed-wrapper>

        <shadowed-wrapper class="flex flex-col h-full p-5 w-full lg:w-1/2">
          <span class="text-gray-dark font-bold text-xl shrink-0">Email Template Editor</span>

          <div class="flex flex-col flex-1 min-h-0 mt-3 gap-3">
            <div class="flex flex-col flex-1 min-h-0">
              <span class="text-gray-dark font-bold mb-2 shrink-0">Header</span>
              <t-textarea v-model="headerText"
                          class="flex-1 min-h-0"
                          :class="getFieldTextClasses('header')"
                          variant="textSection"
                          rows="14"
                          autofocus
                          :disabled="isDisabled"
                          @focus="handleFieldFocus('header')"
                          @blur="handleFieldBlur('header')"
              />
            </div>

            <div class="flex flex-col flex-1 min-h-0">
              <span class="text-gray-dark font-bold mb-2 shrink-0">Footer</span>
              <t-textarea v-model="footerText"
                          class="flex-1 min-h-0"
                          :class="getFieldTextClasses('footer')"
                          variant="textSection"
                          rows="14"
                          :disabled="isDisabled"
                          @focus="handleFieldFocus('footer')"
                          @blur="handleFieldBlur('footer')"
              />
            </div>
          </div>

          <div class="flex justify-end mt-4 gap-2 shrink-0">
            <base-button id="reset-to-last"
                         :variant="isDisabled ? 'transparent-disabled' : 'bordered'"
                         class="text-center w-32 py-2 text-sm"
                         :disabled="isLoading || isDisabled"
                         @click="loadEmailTemplate"
            >
              Reset
            </base-button>
            <base-button id="save"
                         :variant="isDisabled ? 'primary-disabled' : 'primary'"
                         class="text-center w-32 py-2 text-sm justify-center"
                         :disabled="isLoading || isDisabled"
                         @click="saveEmailTemplate"
            >
              Save
            </base-button>
          </div>
        </shadowed-wrapper>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import BotSelector from '@/components/common/BotSelector.vue'
import ShadowedWrapper from '@/components/base-components/ShadowedWrapper.vue'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import botService from '@/services/amio/bot.service'
import emailConfigService from '@/services/amio/email-config.service'
import toaster from '@/utils/toaster'
import { BOT_TYPES } from '@/utils/bot.constants'
import { ensureBotAndMarketLists } from '@/utils/bot-market.utils'
import variableRefsUtils from '@/utils/variable-refs.utils'
import variablesModule from '@/store/modules/variables.module'
import {
  assembleEmailTemplate,
  CONTENT_PLACEHOLDER,
  parseEmailTemplate
} from '@/utils/email-template.utils'

export default {
  name: 'EmailTemplate',

  components: { ShadowedWrapper, BotSelector, AppHeader },

  data() {
    return {
      headerText: '',
      footerText: '',
      templateWrapperOpen: '',
      templateWrapperClose: '',
      contentDivOpen: '',
      contentDivClose: '',
      isLoading: false,
      isDisabled: false,
      focusedField: 'header',
      previewScale: 1,
      previewNaturalWidth: 0,
      previewNaturalHeight: 0,
      contentPlaceholder: CONTENT_PLACEHOLDER
    }
  },

  mounted() {
    this.init()
    this.$nextTick(this.setupPreviewScaling)
  },

  beforeDestroy() {
    this.teardownPreviewScaling()
  },

  computed: {
    botId() {
      return this.$route.params.botId
    },

    emailPreview() {
      return variableRefsUtils.substituteVariableRefs(this.text)
    },

    text() {
      return assembleEmailTemplate({
        templateWrapperOpen: this.templateWrapperOpen,
        templateWrapperClose: this.templateWrapperClose,
        headerText: this.headerText,
        footerText: this.footerText,
        contentDivOpen: this.contentDivOpen,
        contentDivClose: this.contentDivClose
      })
    },

    previewContentStyle() {
      if (!this.previewNaturalWidth || this.previewScale === 1) return {}

      return {
        transform: `scale(${this.previewScale})`,
        transformOrigin: 'top left',
        width: `${this.previewNaturalWidth}px`
      }
    },

    previewViewportStyle() {
      if (!this.previewNaturalWidth) return {}

      return {
        width: `${this.previewNaturalWidth * this.previewScale}px`,
        height: `${this.previewNaturalHeight * this.previewScale}px`
      }
    }
  },

  watch: {
    emailPreview() {
      this.$nextTick(this.updatePreviewScale)
    }
  },

  methods: {
    async init() {
      await ensureBotAndMarketLists()

      const botType = await this.getBotType()
      if (botType !== BOT_TYPES.EMAIL) {
        toaster.error('This bot is not email bot')
        this.isDisabled = true
        return
      }

      await variablesModule.fetchVariables()
      await this.loadEmailTemplate()
    },

    async loadEmailTemplate() {
      if (this.isDisabled) return

      this.isLoading = true
      try {
        const { emailTemplate } = await emailConfigService.get(this.botId)
        this.setTemplateParts(emailTemplate)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.isLoading = false
      }
    },

    async saveEmailTemplate() {
      if (this.isDisabled) return

      this.isLoading = true
      try {
        await emailConfigService.update(this.botId, this.text)
        toaster.success('saved!')
      } catch (err) {
        toaster.error(err)
      } finally {
        this.isLoading = false
      }
    },

    async getBotType() {
      try {
        const { type } = await botService.getBot(this.botId)
        return type
      } catch (err) {
        toaster.error(err)
      }
    },

    emailBotFilter(bot) {
      return bot.type === BOT_TYPES.EMAIL
    },

    getEmailBotLabel(bot) {
      if (!bot.multi_bot_id) return bot.name

      const bots = botModule.getBotList()
      const primaryBot = bots.find(b => b.id === bot.primary_bot_id) || bot
      const market = marketModule.getMarketList().find(m => m.id === bot.market_id)
      const marketName = market?.name

      return marketName ? `${primaryBot.name} - ${marketName}` : primaryBot.name
    },

    changeBot(bot) {
      if (!bot || bot.id === this.botId) return

      this.$router.replace({ params: { botId: bot.id } })
      this.headerText = ''
      this.footerText = ''
      this.templateWrapperOpen = ''
      this.templateWrapperClose = ''
      this.contentDivOpen = ''
      this.contentDivClose = ''
      this.isDisabled = false
      this.init()
    },

    setTemplateParts(emailTemplate) {
      const parts = parseEmailTemplate(emailTemplate)
      this.templateWrapperOpen = parts.templateWrapperOpen
      this.templateWrapperClose = parts.templateWrapperClose
      this.contentDivOpen = parts.contentDivOpen
      this.contentDivClose = parts.contentDivClose
      this.headerText = parts.headerText
      this.footerText = parts.footerText
    },

    getFieldTextClasses(field) {
      if (this.isDisabled) return 'border-soft'
      if (this.focusedField === field) return 'border-focus'
      return 'border-soft'
    },

    handleFieldFocus(field) {
      this.focusedField = field
    },

    handleFieldBlur(field) {
      if (this.focusedField === field) this.focusedField = null
    },

    setupPreviewScaling() {
      if (typeof ResizeObserver === 'undefined') {
        this.updatePreviewScale()
        return
      }

      this.previewResizeObserver = new ResizeObserver(() => {
        this.updatePreviewScale()
      })

      const container = this.$refs.previewContainer
      if (container) this.previewResizeObserver.observe(container)

      this.updatePreviewScale()
    },

    teardownPreviewScaling() {
      if (this.previewResizeObserver) {
        this.previewResizeObserver.disconnect()
        this.previewResizeObserver = null
      }
    },

    updatePreviewScale() {
      const container = this.$refs.previewContainer
      const content = this.$refs.previewContent
      if (!container || !content) return

      this.previewScale = 1
      this.previewNaturalWidth = 0
      this.previewNaturalHeight = 0

      this.$nextTick(() => {
        const naturalWidth = content.scrollWidth
        const naturalHeight = content.scrollHeight
        const availableWidth = container.clientWidth

        if (!naturalWidth || !availableWidth) return

        this.previewScale = Math.min(1, availableWidth / naturalWidth)
        this.previewNaturalWidth = naturalWidth
        this.previewNaturalHeight = naturalHeight

        this.bindPreviewImageLoadHandlers()
      })
    },

    bindPreviewImageLoadHandlers() {
      const content = this.$refs.previewContent
      if (!content) return

      content.querySelectorAll('img').forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', this.updatePreviewScale, { once: true })
        }
      })
    }
  }
}
</script>
